/**
 * Quick Add to Cart Modal
 * 
 * This component creates a modal that allows users to quickly add products to their cart
 * directly from product cards. It handles variant selection, button loading states, and
 * form submission.
 * 
 * Features:
 * - Variant selection through color options
 * - Loading state indicators on buttons
 * - Form submission handling
 * - Dynamic variant modal display based on selected color
 */

if (!customElements.get('quick-add-to-cart-modal')) {
  customElements.define(
    'quick-add-to-cart-modal',
    class QuickAddToCartModal extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.variantButtons = this.querySelectorAll(
          '.quick-add-to-cart__button'
        );
        this.productForm = this.querySelector('product-form');
        this.variantInput = this.querySelector('input[name="id"]');
        this.submitButton = this.querySelector('[type="submit"]');

        this.bindEvents();
      }

      bindEvents() {
        if (!this.variantButtons.length) return;

        this.addEventListener(
          CUSTOM_EVENTS.productCardVariantUpdate,
          this.handleVariantUpdate.bind(this)
        );

        this.variantButtons.forEach((button) => {
          button.addEventListener(
            'click',
            this.handleVariantButtonClick.bind(this)
          );
        });
      }

      handleVariantUpdate(event) {
        const selectedColor = event.detail.selectedColor;

        const variantModals = this.querySelectorAll('[data-variant]');
        variantModals.forEach((variantModal) => {
          variantModal.classList.remove('open');
        });

        const variantModal = this.querySelector(
          `[data-variant="${selectedColor}"]`
        );
        variantModal?.classList.add('open');
      }

      handleVariantButtonClick(event) {
        event.preventDefault();

        const button = event.currentTarget;
        const variantId = button.dataset.variantId;

        if (!variantId || !this.variantInput || !this.submitButton) return;

        // Show loading state on the clicked button
        this.setButtonLoading(button, true);

        // Find the parent drawer-bottom element
        const drawerBottom = this.closest('drawer-bottom');

        // Set up auto-close workflow when product is added to cart
        this._setupAutoCloseWorkflow(drawerBottom);

        // Update the variant ID input and trigger form submission
        this.variantInput.value = variantId;
        this.submitButton.click();

        // Reset button loading state after a delay
        setTimeout(() => {
          this.setButtonLoading(button, false);
        }, 1000);
      }

      /**
       * Sets up automatic closing of quick-add drawer after cart is updated
       * @param {Element} drawerBottom - The drawer element to close
       * @private
       */
      _setupAutoCloseWorkflow(drawerBottom) {
        if (!drawerBottom) return;

        const cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'product-form') {
            cartUpdateUnsubscriber();

            const cartDrawerOpenUnsubscriber = subscribe(PUB_SUB_EVENTS.cartDrawerOpen, () => {
              drawerBottom.close();
              cartDrawerOpenUnsubscriber();
            });
          }
        });
      }

      setButtonLoading(button, isLoading) {
        const spinner = button.querySelector(
          '.quick-add-modal__loading-spinner'
        );

        if (isLoading) {
          button.classList.add('loading');
          if (spinner) spinner.classList.remove('hidden');
        } else {
          button.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        }
      }
    }
  );
}
